import type {
  IAgoraRTCClient,
  ILocalVideoTrack,
  ILocalAudioTrack,
  IAgoraRTCRemoteUser,
  ConnectionState,
  RemoteStreamType,
  ChannelMediaRelayState,
  ChannelMediaRelayError,
  ChannelMediaRelayEvent,
  VideoState
} from 'agora-rtc-react';

import { StudioLogger } from '@studio/logger/StudioLogger';

import debounce from 'lodash.debounce';

import { AGORA_ACTIVE_SPEAKER_INTENSITY_THRESHOLD } from '@studio/constants/ActiveSpeakerInSession';
import { stopScreenShare } from '../screenShare/screenShareHelper';

const LOG = new StudioLogger('agoraEventHandler');

// Not exported from SDK
interface CheckVideoVisibleResult {
  visible: boolean;
  reason?: 'SIZE' | 'STYLE' | 'POSITION' | 'COVERED';
}
/* eslint-disable no-unused-vars */
interface DispatchFunction {
  (action: any): void;
}

/* eslint-disable no-unused-vars */
interface Actions {
  showModal?: (...args: any[]) => any;
  showNotification?: (...args: any[]) => any;
  clearNotification?: (...args: any[]) => any;
  setDesiredAttendeeCameraStatus?: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
  setAttendeeCameraStatus: (...args: any[]) => any;
  setSourceInitializing?: (...args: any[]) => any;
  setAttendeeMuteStatus: (...args: any[]) => any;
  setAttendeeVoiceIntensity: (...args: any[]) => any;
  setAttendeePresence?: (...args: any[]) => any;
  disconnectSource?: (...args: any[]) => any;
  setSourceAsUnassigned?: (...args: any[]) => any;
  updateSource?: (...args: any[]) => any;
  handleAgoraOnStagePriority?: (...args: any[]) => any;
  muteMeetingAudio?: boolean;
}

// See the following wiki for Chime -> Agora event mapping and details
// https://wiki.cvent.com/spaces/STUDIO/pages/962199868/Chime+-+Agora+Event+Mapping+Details
function setupEventListeners(client: IAgoraRTCClient, dispatch: DispatchFunction, actions: Actions) {
  // Store debounced calls and voice intensities for each user
  const userDebouncedCalls = new Map<string | number, ReturnType<typeof debounce>>();

  // User-related events
  client.on('user-joined', (user: IAgoraRTCRemoteUser) => {
    if (actions.setSourceInitializing) {
      dispatch(actions.setSourceInitializing(user.uid, { isInitializing: false }));
    }
    if (actions.setAttendeePresence) {
      dispatch(actions.setAttendeePresence(user.uid, true, actions.disconnectSource));
    }

    if (actions.handleAgoraOnStagePriority) {
      dispatch(actions.handleAgoraOnStagePriority());
    }
    dispatch(actions.setAttendeeMuteStatus(user.uid, true));
    if (!user.uid.toString().includes('#content')) {
      dispatch(actions.setAttendeeCameraStatus(user.uid, false));
    }
    LOG.info(`User entered: ${user.uid}`);
  });

  client.on('user-left', (user: IAgoraRTCRemoteUser, reason: string) => {
    if (actions.setSourceAsUnassigned && actions.updateSource) {
      dispatch(actions.setSourceAsUnassigned(user.uid, actions.updateSource));
    }
    if (actions.setAttendeePresence && actions.disconnectSource) {
      dispatch(actions.setAttendeePresence(user.uid, false, actions.disconnectSource));
    }

    // Clean up debounced calls and voice intensity data for the user who left
    const debouncedCall = userDebouncedCalls.get(user.uid);
    if (debouncedCall) {
      debouncedCall.cancel();
      userDebouncedCalls.delete(user.uid);
    }

    LOG.info(`User left: ${user.uid}, Reason: ${reason}`);
  });
  client.on('user-published', async (user: IAgoraRTCRemoteUser, mediaType: 'video' | 'audio' | 'datachannel') => {
    try {
      await client.subscribe(user, mediaType);

      LOG.info(`Remote user published: ${user.uid}, Media type: ${mediaType}`);

      if (mediaType === 'audio' && user.audioTrack) {
        // Stop the track immediately if muteMeetingAudio is enabled
        if (actions.muteMeetingAudio) {
          LOG.info(`Audio track stopped for ${user.uid} - muteMeetingAudio is enabled`);
          user.audioTrack.stop();
        } else {
          user.audioTrack.play();
          dispatch(actions.setAttendeeMuteStatus(user.uid, false));
        }
      }

      if (user.videoTrack) {
        dispatch(actions.addToAttendeeTiles(user.uid, user.videoTrack));
        if (!user.uid.toString().includes('#content')) {
          dispatch(actions.setAttendeeCameraStatus(user.uid, true));
        }
      }
    } catch (error) {
      LOG.error(`Failed to subscribe to user: ${user.uid}, Media type: ${mediaType}`, error);
      return;
    }
  });
  client.on('user-unpublished', async (user: IAgoraRTCRemoteUser, mediaType: 'video' | 'audio' | 'datachannel') => {
    if (mediaType === 'audio') {
      dispatch(actions.setAttendeeMuteStatus(user.uid, true));
    }
    if (mediaType === 'video') {
      dispatch(actions.setAttendeeCameraStatus(user.uid, false));
    }
    LOG.info(`Remote user unpublished: ${user.uid}, Media type: ${mediaType}`);
  });
  client.on('user-info-updated', (uid: IAgoraRTCRemoteUser['uid'], msg: string) => {
    LOG.info(`User info updated: ${uid}, Message: ${msg}`);
  });

  // Connection-related events
  client.on('connection-state-change', (curState: ConnectionState, revState: ConnectionState, reason: any) => {
    LOG.info('connection-state-change', curState, revState, reason);
  });
  client.on('peerconnection-state-change', (curState: RTCPeerConnectionState, revState: RTCPeerConnectionState) => {
    LOG.info(`Peer connection state changed: ${revState} to ${curState}`);
  });

  // Media reconnection events
  client.on('media-reconnect-start', (uid: IAgoraRTCRemoteUser['uid']) => {
    LOG.info(`Media reconnect start: ${uid}`);
  });
  client.on('media-reconnect-end', async (uid: IAgoraRTCRemoteUser['uid']) => {
    LOG.info(`Media reconnect end: ${uid}`);

    try {
      const remoteUser = client.remoteUsers.find(user => user.uid === uid);
      if (!remoteUser) return;

      // Force re-subscribe to video if remote user has video enabled
      if (remoteUser.hasVideo) {
        // Unsubscribe first if track exists to clean up stale connection
        if (remoteUser.videoTrack) {
          await client.unsubscribe(remoteUser, 'video');
        }

        // Re-subscribe to get fresh video track
        await client.subscribe(remoteUser, 'video');

        if (remoteUser.videoTrack) {
          LOG.info(`Re-established video for remote user: ${uid}`);
          dispatch(actions.addToAttendeeTiles(remoteUser.uid, remoteUser.videoTrack));
          dispatch(actions.setAttendeeCameraStatus(remoteUser.uid, true));
        }
      }

      // Force re-subscribe to audio if remote user has audio enabled
      if (remoteUser.hasAudio) {
        // Unsubscribe first if track exists to clean up stale connection
        if (remoteUser.audioTrack) {
          await client.unsubscribe(remoteUser, 'audio');
        }

        // Re-subscribe to get fresh audio track
        await client.subscribe(remoteUser, 'audio');

        if (remoteUser.audioTrack) {
          LOG.info(`Re-established audio for remote user: ${uid}`);
          remoteUser.audioTrack.play();
          dispatch(actions.setAttendeeMuteStatus(remoteUser.uid, false));
        }
      }
    } catch (error) {
      LOG.error(`Failed to re-establish media for remote user: ${uid}`, error);
    }
  });

  // Stream-related events
  client.on('stream-type-changed', (uid: IAgoraRTCRemoteUser['uid'], streamType: RemoteStreamType) => {
    LOG.info(`Stream type changed: ${uid}, New type: ${streamType}`);
  });
  client.on('stream-fallback', (uid: IAgoraRTCRemoteUser['uid'], isFallbackOrRecover: 'fallback' | 'recover') => {
    LOG.info(`Stream fallback: ${uid}, Status: ${isFallbackOrRecover}`);
  });

  // Channel media relay events
  client.on('channel-media-relay-state', (state: ChannelMediaRelayState, code: ChannelMediaRelayError) => {
    LOG.info(`Channel media relay state: ${state}, Code: ${code}`);
  });
  client.on('channel-media-relay-event', (event: ChannelMediaRelayEvent) => {
    LOG.info(`Channel media relay event: ${JSON.stringify(event)}`);
  });

  // Audio/video quality events
  client.enableAudioVolumeIndicator();
  client.on('volume-indicator', (result: object[]) => {
    result.forEach((volume: any) => {
      const uid = volume.uid;
      const normalizedVolume = volume.level / 100;
      const existingDebouncedCall = userDebouncedCalls.get(uid);
      const isSpeaking = normalizedVolume > AGORA_ACTIVE_SPEAKER_INTENSITY_THRESHOLD;

      if (isSpeaking) {
        // Speaking: cancel any pending stop call and dispatch immediately
        if (existingDebouncedCall) {
          existingDebouncedCall.cancel();
          userDebouncedCalls.delete(uid);
        }
        dispatch(actions.setAttendeeVoiceIntensity({ [uid]: normalizedVolume }));
      } else if (!existingDebouncedCall) {
        // Not speaking: debounce the "stop speaking" dispatch to show the indicator between natural speech lags
        const stopSpeakingCall = debounce(() => {
          dispatch(actions.setAttendeeVoiceIntensity({ [uid]: 0 }));
          userDebouncedCalls.delete(uid);
        }, 1500);

        userDebouncedCalls.set(uid, stopSpeakingCall);
        stopSpeakingCall();
      }
    });
  });

  // Data stream events
  client.on('stream-message', (uid: IAgoraRTCRemoteUser['uid'], payload: Uint8Array) => {
    LOG.info(`Stream message: ${uid}, Payload: ${JSON.stringify(payload)}`);
  });

  // Track-related events
  client.on('track-updated', (track: MediaStreamTrack) => {
    LOG.info(`Track updated: ${track.id}`);
  });
  client.on('track-ended', (track: MediaStreamTrack) => {
    LOG.info(`Track ended: ${track.id}`);
  });

  // Video-specific events
  client.on('video-state-changed', (videoState: VideoState) => {
    LOG.info(`Video state changed: ${JSON.stringify(videoState)}`);
  });
  client.on('video-element-visible-status', (data: CheckVideoVisibleResult) => {
    LOG.info(`Video element visible status: ${JSON.stringify(data)}`);
  });
}

/**
 * Sets up event listeners for screen share video track
 * This is separate from main event listeners because screen share client is created on-demand
 *
 * @param screenVideoTrack - The screen video track to listen to
 * @param screenShareClient - The Agora RTC client instance for screen sharing
 * @param screenAudioTrack - (Optional) The screen audio track
 * @param onScreenShareEnded - Callback function to execute after Agora cleanup when browser stops sharing
 */
function setupScreenShareEventListeners(
  screenVideoTrack: ILocalVideoTrack,
  screenShareClient: IAgoraRTCClient,
  screenAudioTrack: ILocalAudioTrack | undefined,
  onScreenShareEnded: () => void
): void {
  // Listen for track-ended event when the browser stops screen sharing
  // This happens when user clicks the browser's "Stop Sharing" button
  screenVideoTrack.on('track-ended', async () => {
    LOG.info('Screen share track ended');
    // clean up Agora resources
    await stopScreenShare(screenShareClient, screenVideoTrack, screenAudioTrack);
    // call the callback for Redux state and socket event handling
    onScreenShareEnded();
  });

  // Listen for media reconnection events to handle network loss
  screenShareClient.on('media-reconnect-start', (uid: IAgoraRTCRemoteUser['uid']) => {
    LOG.info(`Screen share media reconnect start: ${uid}`);
  });

  screenShareClient.on('media-reconnect-end', async (uid: IAgoraRTCRemoteUser['uid']) => {
    LOG.info(`Screen share media reconnect end: ${uid}`);

    try {
      // Re-publish screen share tracks after network reconnection
      const tracks = screenAudioTrack ? [screenVideoTrack, screenAudioTrack] : [screenVideoTrack];

      // Check if tracks are still valid
      const isVideoTrackValid =
        screenVideoTrack.enabled && screenVideoTrack.getMediaStreamTrack()?.readyState === 'live';

      if (isVideoTrackValid) {
        await screenShareClient.publish(tracks);
        LOG.info(`Re-published screen share tracks after reconnection: ${uid}`);
      }
    } catch (error) {
      LOG.error(`Failed to re-publish screen share tracks after reconnection: ${uid}`, error);
    }
  });
}

export { setupEventListeners, setupScreenShareEventListeners };
